import React, { useEffect, useState } from "react";
import { HiXMark } from "react-icons/hi2";
import PlanBadge from "./PlanBadge";
import StatusBadge from "./StatusBadge";
import { formatCompactNumber, formatWithCommas } from "./format";
import { fetchUserPackageHistory } from "./api";
import { transformPackageHistory } from "./transform";

const UserHistoryModal = ({ user, onClose }) => {
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState("idle");

  useEffect(() => {
    if (!user) return undefined;

    let cancelled = false;
    setStatus("loading");
    setHistory([]);

    fetchUserPackageHistory(user.id)
      .then((data) => {
        if (cancelled) return;
        setHistory(transformPackageHistory(data));
        setStatus("loaded");
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("error");
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!user) return undefined;
    const handleKeyDown = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [user, onClose]);

  if (!user) return null;

  return (
    <div
      className="fixed inset-0 bg-gray-900 bg-opacity-60 flex items-center justify-center p-4 z-50"
      style={{ backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-2xl flex flex-col relative"
        style={{ height: "640px", maxHeight: "85vh" }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`${user.name} subscription history`}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-5 right-5 w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50"
        >
          <HiXMark size={18} aria-hidden="true" />
        </button>

        <div className="px-8 pt-8 pb-4 flex-shrink-0">
          <div className="flex items-start justify-between gap-4 pr-12 flex-wrap">
            <div className="flex items-center gap-4">
              <span className="w-14 h-14 rounded-full bg-indigo-50 text-indigo-600 font-bold flex items-center justify-center text-lg flex-shrink-0">
                {user.initials}
              </span>
              <div>
                <div className="text-xl font-bold text-gray-900">{user.name}</div>
                <div className="text-gray-500">{user.email}</div>
              </div>
            </div>
            <div className="text-right">
              <PlanBadge plan={user.plan} />
              {/* <div className="text-gray-500 text-sm mt-1">{user.billing}</div> */}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <div className="bg-gray-50 rounded-xl px-4 py-4">
              <div className="text-sm text-gray-500">Input tokens</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {formatCompactNumber(user.inputTokens)}
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl px-4 py-4">
              <div className="text-sm text-gray-500">Output tokens</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {formatCompactNumber(user.outputTokens)}
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl px-4 py-4">
              <div className="text-sm text-gray-500">Remaining tokens</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {formatCompactNumber(user.remainingTokens)}
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl px-4 py-4">
              <div className="text-sm text-gray-500">Remaining credits</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {formatWithCommas(user.remainingCredits)}
                {/* <span className="text-gray-400 text-base"> / {formatWithCommas(user.remainingCreditsCap)}</span> */}
              </div>
            </div>
          </div>
        </div>

        <div className="px-8 pb-8 flex-1 flex flex-col" style={{ minHeight: 0 }}>
          <h3
            className="text-base font-bold text-gray-900 mb-2"
            style={{ flexShrink: 0 }}
          >
            Package history
          </h3>
          <div
            className="overflow-y-auto overflow-x-auto rounded-xl border border-gray-100 flex-1"
            style={{ minHeight: 0 }}
          >
            {status === "loading" && (
              <div className="px-4 py-6 text-center text-gray-500 text-sm">Loading history…</div>
            )}
            {status === "error" && (
              <div className="px-4 py-6 text-center text-red-500 text-sm">
                Couldn't load package history.
              </div>
            )}
            {status === "loaded" && history.length === 0 && (
              <div className="px-4 py-6 text-center text-gray-500 text-sm">
                No packages purchased yet.
              </div>
            )}
            {status === "loaded" && history.length > 0 && (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-gray-500 sticky top-0 z-10">
                    <th className="px-4 py-3 font-bold">Date</th>
                    <th className="px-4 py-3 font-bold">Package</th>
                    <th className="px-4 py-3 font-bold text-right">Allocated</th>
                    <th className="px-4 py-3 font-bold text-right">Remaining</th>
                    <th className="px-4 py-3 font-bold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((row, index) => (
                    <tr key={`${row.date}-${index}`} className="border-t border-gray-100">
                      <td className="px-4 py-3 text-gray-900">{row.date}</td>
                      <td className="px-4 py-3 font-bold text-gray-900">{row.package}</td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {formatWithCommas(row.tokensAllocated)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-900">
                        {formatWithCommas(row.tokensRemaining)}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge status={row.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserHistoryModal;
